import { useEffect, useMemo, useState } from "react";
import type { Vehicle } from "./useVehicleStream";
import {
  buildRouteProjection,
  type ProjectedStop,
  type RawRouteGeometry,
  type RouteProjection,
} from "./routeProjection";

// Seoul Metro-style platform displays (PDPs).
//
// One panel per (route, terminus). Inside each panel, one Seoul-Metro
// "platform display" card per intermediate station, showing:
//   • a dark navy header with the station name and a live clock
//   • two big arrival readouts ("NEXT" in cyan, "FOLLOWING" in yellow)
//     with the destination headsign and a mm:ss countdown
//   • a horizontal line diagram of the upstream stops with the arriving
//     station highlighted on the right under a big arrow, plus little
//     boxy train glyphs (labelled with the vehicle id and headsign)
//     parked above the line at each approaching train's current position
//
// CSS transitions give the trains the slow, deliberate motion that makes
// the displays feel alive even between SSE snapshots (which arrive every
// ~10 seconds).

const PREV_STOPS = 5;
const NEXT_STOPS = 1;
// Drop trains whose latest sample is older than this — usually means the
// vehicle dropped off the feed.
const STALE_AFTER_SEC = 120;
// Rough cruise speed used to estimate ETAs from a train's remaining
// along-route distance to a station. Link trains average ~30 mph between
// stations including dwell, which is ~13.4 m/s. Close enough for the
// "next train in 3:42" countdown style readout — these are not booked
// arrival times.
const CRUISE_M_PER_S = 13.4;
// Trains farther than this from the station (along the route) are not
// considered for the next/following readout — they're effectively not
// "approaching" yet from the rider's point of view.
const APPROACH_HORIZON_M = 25_000;

export type PanelSpec = {
  routeId: string;
  terminus: string;
  headsignMatch: string[];
};

export const PANELS: PanelSpec[] = [
  { routeId: "40_100479", terminus: "Lynnwood", headsignMatch: ["lynnwood"] },
  {
    routeId: "40_100479",
    terminus: "Federal Way",
    headsignMatch: ["federal way", "angle lake"],
  },
  { routeId: "40_2LINE", terminus: "Lynnwood", headsignMatch: ["lynnwood"] },
  { routeId: "40_2LINE", terminus: "Redmond", headsignMatch: ["redmond"] },
];

export function matchesPanel(headsign: string | undefined, panel: PanelSpec): boolean {
  if (!headsign) return false;
  const h = headsign.toLowerCase();
  return panel.headsignMatch.some((m) => h.includes(m));
}

type Props = {
  vehicles: Vehicle[];
  connected: boolean;
  /** True when the parent is showing a historical (scrubbed) snapshot. */
  historical?: boolean;
};

export function PlatformDisplay({ vehicles, connected, historical = false }: Props) {
  const [projections, setProjections] = useState<RouteProjection[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  // Tick once a second so freshness readouts stay current and CSS
  // transitions on train markers don't get pinned in place when a snapshot
  // doesn't arrive. Frozen while viewing history so the clock and timing
  // labels reflect the scrubbed-to instant.
  const [, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    if (historical) return;
    const id = window.setInterval(
      () => setNow(Math.floor(Date.now() / 1000)),
      1000,
    );
    return () => window.clearInterval(id);
  }, [historical]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/routes/geometry");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as RawRouteGeometry[];
        if (cancelled) return;
        const projs = data
          .map(buildRouteProjection)
          .filter((p) => p.lengthM > 0);
        projs.sort((a, b) =>
          a.shortName.localeCompare(b.shortName, undefined, { numeric: true }),
        );
        setProjections(projs);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const panels = useMemo(() => {
    if (!projections) return [];
    const projByRoute = new Map(projections.map((p) => [p.routeId, p]));
    // When viewing history, the parent passes a reconstructed snapshot whose
    // sample timestamps are the scrubbed-to instant; we use that as "now"
    // for staleness checks instead of wall-clock time.
    const refSec = historical
      ? vehicles.reduce((m, v) => Math.max(m, v.ts || 0), 0) ||
        Math.floor(Date.now() / 1000)
      : Math.floor(Date.now() / 1000);
    return PANELS.map((panel) => {
      const projection = projByRoute.get(panel.routeId);
      if (!projection) return { panel, projection: null, trains: [], orientedStops: [] };

      // Choose travel direction: terminus is whichever end-stop fuzzy-matches
      // the panel's terminus name. Stops are then re-ordered so index 0 is
      // the origin and the last entry is the destination — this lets every
      // row share the same "leftmost stop -> rightmost (arriving) station"
      // mental model.
      const direction = inferDirection(projection.stops, panel.terminus);
      const orientedStops =
        direction === "forward"
          ? projection.stops.slice()
          : projection.stops.slice().reverse();

      const trains = collectTrains(vehicles, projection, panel, refSec);
      return { panel, projection, trains, orientedStops };
    });
  }, [projections, vehicles, historical]);

  if (error) {
    return (
      <div className="platform-empty">
        Couldn't load route geometry: {error}
      </div>
    );
  }
  if (!projections) {
    return <div className="platform-empty">Loading route geometry…</div>;
  }
  if (projections.length === 0) {
    return <div className="platform-empty">No routes available.</div>;
  }

  return (
    <div className="platform">
      <div className="platform-header">
        <div className="platform-title">
          Seoul Metro · {historical ? "history" : "live arrivals"}
        </div>
        <div
          className={`platform-status ${historical ? "history" : connected ? "ok" : "wait"}`}
        >
          {historical ? "◐ history" : connected ? "● live" : "○ waiting…"}
        </div>
      </div>
      <div className="platform-scroll">
        {panels.map(({ panel, projection, trains, orientedStops }) => {
          if (!projection) {
            return (
              <section
                key={`${panel.routeId}-${panel.terminus}`}
                className="platform-panel"
              >
                <header className="platform-panel-header">
                  <span className="platform-panel-route">{panel.routeId}</span>
                  <span className="platform-panel-arrow">→</span>
                  <span className="platform-panel-terminus">
                    {panel.terminus}
                  </span>
                  <span className="platform-panel-meta">no geometry</span>
                </header>
              </section>
            );
          }

          // A station "row" is a Seoul-Metro PDP for that station. We
          // skip the very first stop (no upstream context) and the
          // destination terminus (trains depart from it, never arrive).
          const stationRows = orientedStops.slice(1, -1);

          // Direction sign of travel along the canonical projection: +1
          // if "forward" (orientedStops matches projection.stops), -1 if
          // reversed. We use it to convert a train's projected distance
          // into "remaining distance to station" along its direction of
          // travel, regardless of which way the polyline was digitized.
          const dirSign =
            orientedStops.length >= 2 &&
            orientedStops[0].distM > orientedStops[orientedStops.length - 1]
              .distM
              ? -1
              : 1;

          return (
            <section
              key={`${panel.routeId}-${panel.terminus}`}
              className="platform-panel"
              style={
                {
                  "--route-color": projection.color,
                } as React.CSSProperties
              }
            >
              <header className="platform-panel-header">
                <span
                  className="platform-panel-swatch"
                  style={{ background: projection.color }}
                  aria-hidden
                />
                <span className="platform-panel-route">
                  {projection.shortName || projection.longName}
                </span>
                <span className="platform-panel-arrow">→</span>
                <span className="platform-panel-terminus">
                  {panel.terminus}
                </span>
                <span className="platform-panel-meta">
                  {trains.length}{" "}
                  {trains.length === 1 ? "train" : "trains"} in service
                </span>
              </header>
              <div className="platform-rows">
                {stationRows.map((station, idx) => {
                  // The PREV_STOPS stops upstream and NEXT_STOPS stops
                  // downstream of this station. orientedStops index of
                  // `station` is `idx + 1` (we sliced off the first
                  // stop).
                  const stationIdx = idx + 1;
                  const upstream: ProjectedStop[] = [];
                  for (
                    let i = Math.max(0, stationIdx - PREV_STOPS);
                    i < stationIdx;
                    i++
                  ) {
                    upstream.push(orientedStops[i]);
                  }
                  const downstream: ProjectedStop[] = [];
                  for (
                    let i = stationIdx + 1;
                    i <
                    Math.min(orientedStops.length, stationIdx + 1 + NEXT_STOPS);
                    i++
                  ) {
                    downstream.push(orientedStops[i]);
                  }
                  return (
                    <PlatformCard
                      key={station.id}
                      routeShortName={
                        projection.shortName || projection.longName
                      }
                      terminus={panel.terminus}
                      station={station}
                      upstream={upstream}
                      downstream={downstream}
                      trains={trains}
                      dirSign={dirSign}
                      color={projection.color}
                    />
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

export function inferDirection(
  stops: ProjectedStop[],
  terminus: string,
): "forward" | "reverse" {
  if (stops.length < 2) return "forward";
  const t = terminus.toLowerCase();
  // Look at the first 3 stops vs last 3 stops; whichever cluster contains
  // the terminus name wins. Falls back to "forward" if neither matches.
  const head = stops.slice(0, 3).some((s) => s.name.toLowerCase().includes(t));
  const tail = stops.slice(-3).some((s) => s.name.toLowerCase().includes(t));
  if (tail && !head) return "forward";
  if (head && !tail) return "reverse";
  return "forward";
}

export type Train = {
  vehicleId: string;
  distM: number;
  ts: number;
  headsign?: string;
};

export function collectTrains(
  vehicles: Vehicle[],
  proj: RouteProjection,
  panel: PanelSpec,
  nowSec: number,
): Train[] {
  const out: Train[] = [];
  for (const v of vehicles) {
    if (!v.vehicle_id || v.route_id !== proj.routeId) continue;
    if (!matchesPanel(v.trip_headsign, panel)) continue;
    const ts = v.ts || nowSec;
    if (nowSec - ts > STALE_AFTER_SEC) continue;
    const distM = proj.project(v.lat, v.lon);
    if (distM == null) continue;
    out.push({
      vehicleId: v.vehicle_id,
      distM,
      ts,
      headsign: v.trip_headsign,
    });
  }
  return out;
}

function PlatformCard({
  routeShortName,
  terminus,
  station,
  upstream,
  downstream,
  trains,
  dirSign,
  color,
}: {
  routeShortName: string;
  terminus: string;
  station: ProjectedStop;
  upstream: ProjectedStop[];
  downstream: ProjectedStop[];
  trains: Train[];
  dirSign: 1 | -1;
  color: string;
}) {
  // Geometry of the line strip: leftmost rendered stop is upstream[0],
  // rightmost is the last downstream stop (or the arriving station if
  // there is no downstream context).
  const leftStop = upstream[0] ?? station;
  const rightStop = downstream[downstream.length - 1] ?? station;
  const origin = leftStop.distM;
  const span = rightStop.distM - origin; // signed; same sign as dirSign
  const arriving = station.distM;

  const allStops = [...upstream, station, ...downstream];
  const stopFracs = allStops.map((s) => fractionOf(s.distM, origin, span));
  const stationFrac = fractionOf(arriving, origin, span);

  // Trains anywhere upstream of (and approaching) this station, with
  // their remaining distance measured along the train's direction of
  // travel. We use this for both the "next/following" countdown panel
  // and the train glyphs riding the line strip.
  const approaching = trains
    .map((t) => {
      const remainingM = (arriving - t.distM) * dirSign;
      const etaSec = remainingM / CRUISE_M_PER_S;
      return { ...t, remainingM, etaSec };
    })
    .filter((t) => t.remainingM > -200 && t.remainingM <= APPROACH_HORIZON_M)
    .sort((a, b) => a.remainingM - b.remainingM);

  const next = approaching[0];
  const following = approaching[1];

  // Trains currently visible on the strip (within the upstream window),
  // mapped to a 0..1 fractional position along the rendered line.
  const visibleTrains = trains
    .map((t) => ({ ...t, frac: fractionOf(t.distM, origin, span) }))
    .filter((t) => t.frac >= -0.05 && t.frac <= 1.02)
    .sort((a, b) => a.frac - b.frac);

  return (
    <article className="pdp" style={{ "--pdp-line": color } as React.CSSProperties}>
      <header className="pdp-header">
        <div className="pdp-brand">
          <span className="pdp-brand-mark" aria-hidden>
            ◎
          </span>
          <span className="pdp-brand-text">
            <span className="pdp-brand-line1">Sound Transit</span>
            <span className="pdp-brand-line2">Link · Line {routeShortName}</span>
          </span>
        </div>
        <div className="pdp-station">
          <span className="pdp-station-name">{station.name}</span>
          <span className="pdp-station-sub">This station · next trains</span>
        </div>
        <div className="pdp-clock">{formatClock(new Date())}</div>
      </header>

      <div className="pdp-arrivals">
        <ArrivalRow
          label="NEXT"
          tone="next"
          color={color}
          terminus={terminus}
          eta={next?.etaSec}
        />
        <ArrivalRow
          label="FOLLOWING"
          tone="following"
          color={color}
          terminus={terminus}
          eta={following?.etaSec}
        />
      </div>

      <div className="pdp-strip">
        <div className="pdp-track" aria-hidden>
          <div className="pdp-track-line" style={{ background: color }} />
          {stopFracs.map((f, i) => {
            const isStation = allStops[i].id === station.id;
            return (
              <div
                key={allStops[i].id}
                className={`pdp-stop ${isStation ? "is-station" : ""}`}
                style={{
                  left: `${f * 100}%`,
                  borderColor: color,
                  background: isStation ? color : undefined,
                }}
              />
            );
          })}
          {visibleTrains.map((t) => (
            <div
              key={t.vehicleId}
              className="pdp-train"
              style={{
                left: `${Math.max(0, Math.min(1, t.frac)) * 100}%`,
              }}
              title={`Vehicle ${t.vehicleId}${t.headsign ? ` → ${t.headsign}` : ""}`}
            >
              <div className="pdp-train-tag">{terminus}-bound</div>
              <div className="pdp-train-box" style={{ background: color }}>
                {shortVehicleId(t.vehicleId)}
              </div>
            </div>
          ))}
          <div className="pdp-arrow" aria-hidden style={{ left: `${stationFrac * 100}%` }}>
            ▼
          </div>
        </div>
        <div className="pdp-labels">
          {allStops.map((s, i) => {
            const isStation = s.id === station.id;
            return (
              <div
                key={s.id}
                className={`pdp-label ${isStation ? "is-station" : ""}`}
                style={{ left: `${stopFracs[i] * 100}%` }}
              >
                {s.name}
              </div>
            );
          })}
        </div>
      </div>
    </article>
  );
}

function ArrivalRow({
  label,
  tone,
  color,
  terminus,
  eta,
}: {
  label: string;
  tone: "next" | "following";
  color: string;
  terminus: string;
  eta: number | undefined;
}) {
  const hasTrain = eta != null && Number.isFinite(eta);
  return (
    <div className={`pdp-arrival pdp-arrival-${tone}`}>
      <span className="pdp-arrival-label">{label}</span>
      <span className="pdp-arrival-dest">
        <span className="pdp-arrival-dest-name">{terminus}</span>
        <span className="pdp-arrival-dest-suffix">bound</span>
      </span>
      <span className="pdp-arrival-eta">
        {hasTrain ? formatEta(eta!) : "— : —"}
      </span>
      <span
        className="pdp-arrival-pip"
        style={{ background: color }}
        aria-hidden
      />
    </div>
  );
}

function formatClock(d: Date): string {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

export function formatEta(sec: number): string {
  if (sec <= 5) return "ARR";
  const total = Math.max(0, Math.round(sec));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function fractionOf(value: number, origin: number, span: number): number {
  if (span === 0) return 0;
  return (value - origin) / span;
}

export function shortVehicleId(id: string): string {
  // OneBusAway vehicle ids look like "1_4001"; show just the trailing
  // numeric portion to mimic the boxy "6089" car-number tag in the
  // Seoul Metro PDPs.
  const m = id.match(/(\d{3,})\s*$/);
  return m ? m[1] : id.slice(-4);
}
